import React, { useState, useEffect, useCallback } from 'react';
import { LeadMember, MemberSearchResult } from '../../types/Member';
import { memberService } from '../../services/memberService';
import { useAuth } from '../../contexts/AuthContext';
import { MemberSearch } from './MemberSearch';

interface MemberManagerProps {
  leadId: string;
  members?: LeadMember[];
  onMembersUpdate?: () => void;
  compact?: boolean;
  showAddButton?: boolean;
  memberRefreshTrigger?: number;
  onMemberChange?: (leadId: string, isMember: boolean) => void;
}

export const MemberManager: React.FC<MemberManagerProps> = ({
  leadId,
  members: propMembers,
  onMembersUpdate,
  compact = false,
  showAddButton = true,
  memberRefreshTrigger,
  onMemberChange
}) => {
  const { user } = useAuth();
  const [members, setMembers] = useState<LeadMember[]>(propMembers || []);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Memoized member loading function
  const loadMembers = useCallback(async () => {
    if (!propMembers && leadId) {
      try {
        const loadedMembers = await memberService.getLeadMembers(leadId, true);
        setMembers(loadedMembers);
      } catch (err) {
        console.error('Failed to load members:', err);
      }
    }
  }, [leadId, propMembers]);

  // Load members if not provided via props
  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Update local state when prop members change
  useEffect(() => {
    if (propMembers) {
      setMembers(propMembers);
    }
  }, [propMembers]);

  // Memoized refresh function
  const refreshMembers = useCallback(async () => {
    if (leadId) {
      try {
        const refreshedMembers = await memberService.getLeadMembers(leadId, false);
        setMembers(refreshedMembers);
      } catch (err) {
        console.error('Failed to refresh members:', err);
      }
    }
  }, [leadId]);

  // Force refresh when memberRefreshTrigger changes
  useEffect(() => {
    if (memberRefreshTrigger !== undefined && memberRefreshTrigger > 0) {
      refreshMembers();
    }
  }, [memberRefreshTrigger, refreshMembers]);

  const handleAddMember = useCallback(async (selectedMember: MemberSearchResult) => {
    // Input validation
    if (!user) {
      setError('You must be logged in to add members');
      return;
    }

    if (!leadId) {
      setError('Invalid lead ID');
      return;
    }

    if (!selectedMember?.uid) {
      setError('Invalid user selection');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if user is already a member
      const isAlreadyMember = await memberService.isMemberOfLead(leadId, selectedMember.uid);
      if (isAlreadyMember) {
        setError('User is already a member of this lead');
        return;
      }

      await memberService.addMemberToLead({
        leadId,
        memberUid: selectedMember.uid,
        addedBy: user.uid
      });

      // Refresh members and notify parent
      await refreshMembers();
      onMembersUpdate?.();

      // Notify parent about member change if callback is provided
      if (onMemberChange) {
        // Check if the added member is the current user
        if (selectedMember.uid === user.uid) {
          onMemberChange(leadId, true);
        }
      }

      setIsAddingMember(false);
      setError(null);
    } catch (err: any) {
      console.error('❌ Failed to add member:', err);
      setError(err.message || 'Failed to add member');
    } finally {
      setIsLoading(false);
    }
  }, [user, leadId, refreshMembers, onMembersUpdate, onMemberChange]);

  const handleRemoveMember = useCallback(async (memberUid: string) => {
    // Input validation
    if (!user) {
      setError('You must be logged in to remove members');
      return;
    }

    if (!leadId) {
      setError('Invalid lead ID');
      return;
    }

    if (!memberUid) {
      setError('Invalid member ID');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await memberService.removeMemberFromLead({
        leadId,
        memberUid: memberUid,
        removedBy: user.uid
      });

      // Refresh members and notify parent
      await refreshMembers();
      onMembersUpdate?.();

      // Notify parent about member change if callback is provided
      if (onMemberChange) {
        // Check if the removed member is the current user
        if (memberUid === user.uid) {
          onMemberChange(leadId, false);
        }
      }

      setError(null);
    } catch (err: any) {
      console.error('❌ Failed to remove member:', err);
      setError(err.message || 'Failed to remove member');
    } finally {
      setIsLoading(false);
    }
  }, [user, leadId, refreshMembers, onMembersUpdate, onMemberChange]);

  const canRemoveMember = useCallback((member: LeadMember): boolean => {
    if (!user) return false;
    // Users can remove themselves or if they added the member
    return member.uid === user.uid || member.addedBy === user.uid;
  }, [user]);

  // Memoize excluded UIDs to prevent unnecessary re-calculations
  const excludeUids = React.useMemo(() => members.map(m => m.uid), [members]);

  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'wrap',
        position: 'relative'
      }}>
        {/* Member badges */}
        {members.map((member) => (
          <div key={member.uid} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            backgroundColor: 'rgba(103, 126, 234, 0.08)',
            border: '1px solid rgba(103, 126, 234, 0.2)',
            borderRadius: '14px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {member.photoURL ? (
              <img
                src={member.photoURL}
                alt={member.displayName}
                style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <div style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                backgroundColor: '#667eea',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                fontWeight: '600',
                color: 'white'
              }}>
                {member.displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <span style={{ color: '#374151' }}>
              {member.displayName.split(' ')[0]}
            </span>
            {canRemoveMember(member) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveMember(member.uid);
                }}
                disabled={isLoading}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  fontSize: '14px',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: isLoading ? 0.5 : 1,
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#9ca3af';
                }}
                title="Remove member"
              >
                ×
              </button>
            )}
          </div>
        ))}

        {/* Add member button */}
        {showAddButton && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsAddingMember(!isAddingMember);
            }}
            style={{
              border: '1px dashed #d1d5db',
              background: 'white',
              cursor: 'pointer',
              padding: '3px 8px',
              borderRadius: '14px',
              fontSize: '12px',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#667eea';
              e.currentTarget.style.color = '#667eea';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.color = '#6b7280';
            }}
            title="Add member"
          >
            + Add
          </button>
        )}

        {/* Add member dropdown */}
        {isAddingMember && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            minWidth: '320px'
          }}>
            {/* Modal header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#374151',
                margin: 0
              }}>
                Add Team Member
              </h3>
              <button
                onClick={() => setIsAddingMember(false)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  fontSize: '20px',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#9ca3af';
                }}
              >
                ×
              </button>
            </div>

            <MemberSearch
              onSelectMember={handleAddMember}
              onClose={() => setIsAddingMember(false)}
              excludeUids={excludeUids}
              placeholder="Search users..."
            />
            {error && (
              <div style={{
                marginTop: '12px',
                fontSize: '12px',
                color: '#dc2626',
                padding: '8px 12px',
                backgroundColor: '#fef2f2',
                borderRadius: '6px',
                border: '1px solid #fecaca'
              }}>
                {error}
              </div>
            )}
          </div>
        )}

        {/* Modal backdrop */}
        {isAddingMember && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 999
            }}
            onClick={() => setIsAddingMember(false)}
          />
        )}
      </div>
    );
  }

  // Full view (not compact)
  return (
    <div style={{ width: '100%' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <h4 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#374151',
          margin: 0
        }}>
          Members ({members.length})
        </h4>
        {showAddButton && (
          <button
            onClick={() => setIsAddingMember(!isAddingMember)}
            style={{
              border: '1px solid #d1d5db',
              background: 'white',
              cursor: 'pointer',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#374151',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#667eea';
              e.currentTarget.style.color = '#667eea';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.color = '#374151';
            }}
          >
            {isAddingMember ? 'Cancel' : '+ Add Member'}
          </button>
        )}
      </div>

      {isAddingMember && (
        <div style={{ marginBottom: '16px' }}>
          <MemberSearch
            onSelectMember={handleAddMember}
            onClose={() => setIsAddingMember(false)}
            excludeUids={excludeUids}
          />
          {error && (
            <div style={{
              marginTop: '8px',
              fontSize: '12px',
              color: '#dc2626',
              padding: '6px 10px',
              backgroundColor: '#fef2f2',
              borderRadius: '6px',
              border: '1px solid #fecaca'
            }}>
              {error}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {members.map((member) => (
          <div key={member.uid} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #f3f4f6',
            transition: 'background-color 0.2s'
          }}>
            {member.photoURL ? (
              <img
                src={member.photoURL}
                alt={member.displayName}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '1px solid #e5e7eb'
                }}
              />
            ) : (
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#667eea',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '600',
                color: 'white'
              }}>
                {member.displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '2px'
              }}>
                {member.displayName}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6b7280'
              }}>
                {member.email}
                {member.username && ` • @${member.username}`}
              </div>
            </div>
            {canRemoveMember(member) && (
              <button
                onClick={() => handleRemoveMember(member.uid)}
                disabled={isLoading}
                style={{
                  border: '1px solid #fecaca',
                  background: '#fef2f2',
                  cursor: 'pointer',
                  color: '#dc2626',
                  fontSize: '12px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  opacity: isLoading ? 0.5 : 1,
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#fee2e2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fef2f2';
                }}
                title="Remove member"
              >
                Remove
              </button>
            )}
          </div>
        ))}

        {members.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '16px',
            color: '#6b7280',
            fontSize: '13px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #f3f4f6'
          }}>
            No members assigned
          </div>
        )}
      </div>
    </div>
  );
};